// src/pages/ResetPasswordPage.tsx
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { APP_CONFIG } from '../config/constants';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Estados para validação
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');

    useEffect(() => {
        if (!token) {
            setError('Token de recuperação não fornecido. Solicite um novo link de recuperação.');
        }
    }, [token]);

    // Validação de senha
    const validatePassword = (value: string) => {
        if (value.length === 0) {
            setPasswordError('');
            return false;
        }
        if (value.length < 6) {
            setPasswordError('Senha deve ter pelo menos 6 caracteres');
            return false;
        }
        if (value.length > 32) {
            setPasswordError('Senha deve ter no máximo 32 caracteres');
            return false;
        }
        if (!/[a-z]/.test(value)) {
            setPasswordError('Senha deve conter pelo menos uma letra minúscula');
            return false;
        }
        if (!/[A-Z]/.test(value)) {
            setPasswordError('Senha deve conter pelo menos uma letra maiúscula');
            return false;
        }
        if (!/[0-9]/.test(value)) {
            setPasswordError('Senha deve conter pelo menos um número');
            return false;
        }
        if (!/[!@#$%^&*()_+=[\]{};':"\\|,.<>?-]/.test(value)) {
            setPasswordError('Senha deve conter pelo menos um caractere especial');
            return false;
        }
        setPasswordError('');
        return true;
    };

    const validateConfirmPassword = (value: string) => {
        if (value.length === 0) {
            setConfirmPasswordError('');
            return false;
        }
        if (value !== newPassword) {
            setConfirmPasswordError('As senhas não coincidem');
            return false;
        }
        setConfirmPasswordError('');
        return true;
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewPassword(value);
        validatePassword(value);
        if (confirmPassword) {
            validateConfirmPassword(confirmPassword);
        }
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setConfirmPassword(value);
        validateConfirmPassword(value);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!token) {
            setError('Token de recuperação inválido.');
            return;
        }

        const isPasswordValid = validatePassword(newPassword);
        const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);

        if (!isPasswordValid || !isConfirmPasswordValid) {
            setError('Por favor, corrija os erros antes de continuar.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }

        setLoading(true);

        try {
            const response = await apiClient.post('/auth/reset-password', {
                token,
                newPassword
            });
            setSuccess(response.data.message);

            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            if (err.response?.data?.tokenExpired) {
                setError('Link de recuperação expirado. Solicite um novo link de recuperação.');
            } else if (err.response?.data?.invalidToken) {
                setError('Link de recuperação inválido ou já utilizado. Solicite um novo link.');
            } else {
                setError(err.response?.data?.error || 'Erro ao redefinir senha. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-brand-blue-light via-background to-white flex items-center justify-center p-4">
            <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
                <div className="hidden lg:flex flex-col justify-center space-y-6 px-8">
                    <div className="space-y-4">
                        <h2 className="text-4xl font-bold text-text-primary leading-tight">
                            Crie uma nova <span className="text-brand-blue">senha segura</span>
                        </h2>
                        <p className="text-lg text-text-secondary leading-relaxed">
                            Escolha uma senha forte para proteger sua conta na {APP_CONFIG.storeName}.
                        </p>
                    </div>

                    <div className="space-y-4 pt-8">
                        <div className="bg-white/60 backdrop-blur-sm p-5 rounded-xl shadow-subtle">
                            <h3 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
                                <svg className="w-5 h-5 text-brand-blue" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                Requisitos da Senha:
                            </h3>
                            <ul className="space-y-2 text-sm text-text-secondary">
                                <li className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-brand-blue rounded-full"></span>
                                    6 a 32 caracteres
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-brand-blue rounded-full"></span>
                                    Pelo menos uma letra maiúscula
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-brand-blue rounded-full"></span>
                                    Pelo menos uma letra minúscula
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-brand-blue rounded-full"></span>
                                    Pelo menos um número
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-brand-blue rounded-full"></span>
                                    Pelo menos um caractere especial
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="w-full">
                    <div className="bg-white p-8 md:p-10 rounded-2xl shadow-subtle-hover">
                        <div className="mb-8">
                            <h1 className="text-3xl font-bold text-text-primary mb-2">Redefinir Senha</h1>
                            <p className="text-text-secondary">Digite sua nova senha</p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-red-800 flex-1">{error}</p>
                            </div>
                        )}

                        {success && (
                            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
                                <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1">
                                    <p className="text-sm text-green-800 font-medium">{success}</p>
                                    <p className="text-xs text-green-700 mt-1">Redirecionando para a página de login...</p>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label htmlFor="newPassword" className="block text-sm font-semibold text-text-primary mb-2">
                                    Nova Senha
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="newPassword"
                                        value={newPassword}
                                        onChange={handlePasswordChange}
                                        required
                                        disabled={loading || !!success}
                                        className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                                            passwordError 
                                                ? 'border-red-300 focus:ring-red-500' 
                                                : newPassword && !passwordError 
                                                ? 'border-green-300 focus:ring-brand-blue' 
                                                : 'border-border-color focus:ring-brand-blue'
                                        }`}
                                        placeholder="Digite sua nova senha"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-text-secondary hover:text-text-primary transition-colors"
                                    >
                                        {showPassword ? (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            </svg>
                                        ) : (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {passwordError && <p className="mt-1 text-xs text-red-600">{passwordError}</p>}
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-text-primary mb-2">
                                    Confirmar Nova Senha
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        id="confirmPassword"
                                        value={confirmPassword}
                                        onChange={handleConfirmPasswordChange}
                                        required
                                        disabled={loading || !!success}
                                        className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                                            confirmPasswordError 
                                                ? 'border-red-300 focus:ring-red-500' 
                                                : confirmPassword && !confirmPasswordError 
                                                ? 'border-green-300 focus:ring-brand-blue' 
                                                : 'border-border-color focus:ring-brand-blue'
                                        }`}
                                        placeholder="Digite a senha novamente"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-text-secondary hover:text-text-primary transition-colors"
                                    >
                                        {showConfirmPassword ? (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            </svg>
                                        ) : (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {confirmPasswordError && <p className="mt-1 text-xs text-red-600">{confirmPasswordError}</p>}
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !!success || !token}
                                className="w-full bg-brand-blue text-white py-3.5 rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed shadow-sm hover:shadow-md transform hover:-translate-y-0.5 active:translate-y-0"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Redefinindo...
                                    </span>
                                ) : 'Redefinir Senha'}
                            </button>
                        </form>

                        <div className="mt-8 pt-6 border-t border-border-color">
                            <p className="text-center text-sm text-text-secondary">
                                Lembrou sua senha?{' '}
                                <Link to="/login" className="font-semibold text-brand-blue hover:text-blue-700 hover:underline transition-colors">
                                    Fazer login
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

